import './Finance.css';
import { useInitialMonth, orderMonthOffset } from './usePinnedOrderMonth.js';
/**
 * Раздел «Финансы» — §4.
 *
 *   Доход месяца        = Выручка − Себестоимость − Аренда
 *   Зарплата сотрудника = Доход × (процент / 100) + премия
 *   Остаток владельца   = Доход − Σ зарплат
 *
 * Выручка считается по ДАТЕ ПОЛУЧЕНИЯ заказа, независимо от статуса и факта
 * оплаты: это план по объёму принятой работы, а не касса. Вся арифметика —
 * в `@otc/shared/finance` и покрыта тестами.
 *
 * Раздел показан ЦЕПОЧКОЙ, а не набором отдельных плиток: четыре числа рядом
 * друг с другом не объясняли, откуда берётся доход, — приходилось держать
 * формулу в голове. Здесь каждая строка вычитается из предыдущей, и расчёт
 * читается сверху вниз до последней цифры «остаётся вам».
 *
 * Доступ — ТОЛЬКО владельцу. Прежде раздел открывался сотруднику с флагом
 * `salaryAccess`; лаборатория говорит, что в финансы не заходит никто, кроме
 * владельца, поэтому отдельного разрешения больше нет — ни в меню, ни на
 * сервере. Проверка здесь дублирует серверную: интерфейс скрывать мало.
 */

import { useMemo, useState } from 'react';
import { Check, Pencil, Wallet, X } from 'lucide-react';
import {
  formatKop,
  formatMonthKey,
  formatPercent,
  isFirstMonth,
  monthKeyForOffset,
  monthKeysUpTo,
  monthTotals,
  monthlyTrend,
  taxForMonth,
  payroll,
  rublesToKop,
  todayIso,
  trendPercent,
} from '@otc/shared';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '../api/client.js';
import { useBootstrap, useInvoices, useMe, useOrders, useMonthExpenses,
  useSalaryOverrides } from '../api/queries.js';
import { useDrafts } from '../store/drafts.js';
import { useModule } from '../store/useModule.js';
import { ErrorState, SectionHeader, Spinner, Toast, useToast } from '../components/ui.js';
import { useTableZoom } from '../components/useTableZoom.js';
import { ZoomControl } from '../components/ZoomControl.js';
import { FINANCE_COLORS, STATUS_COLORS } from '../theme.js';
import { MonthSwitcher } from '../components/MonthSwitcher.js';
import { manualSalaryInput, salariesForMonth } from './finance-salaries.js';

export function Finance() {
  const today = todayIso();
  const initialMonth = useInitialMonth(today);
  const [offset, setOffset] = useState(() => orderMonthOffset(today, initialMonth));

  /**
   * Смена месяца закрывает открытый ввод зарплаты.
   *
   * Сумма записывается в ТОТ месяц, который открыт сейчас. Оставь поле
   * открытым при переходе стрелкой — и введённое за июль ушло бы в июнь,
   * причём молча: на экране всё то же поле с тем же числом.
   */
  const сменитьМесяц = (шаг: number) => {
    setOffset((v) => v + шаг);
    setEditSalary(null);
  };
  const [span, setSpan] = useState(6);
  /** Год сводной таблицы. По умолчанию — текущий. */
  const [year, setYear] = useState(() => Number(initialMonth.slice(0, 4)));

  const me = useMe();
  const bootstrap = useBootstrap(true);
  // Финансы считаются по всем заказам: нужен ряд за несколько месяцев.
  const ordersQuery = useOrders(null);
  /* Счета нужны для налога: его база — выставленные документы, а не заказы. */
  const invoicesQuery = useInvoices(true);
  const module = useModule('finance');
  const { toast, show } = useToast();

  /* Масштаб рабочей области: у каждого раздела свой, запоминается в браузере. */
  const zoom = useTableZoom('finance', true);

  const settings = bootstrap.data?.settings;

  /**
   * Аренда и минимальная зарплата правятся как часть настроек, но живут в
   * разделе «Финансы» (§6): обе величины участвуют в расчёте на этом же
   * экране, и искать их в другом разделе пришлось бы вслепую.
   */
  /**
   * Фактические зарплаты, назначенные вручную.
   *
   * Живут отдельно от справочника сотрудников: это не свойство человека,
   * а событие месяца. За июль выплачено 102 800, за август 84 450 — и ни
   * то, ни другое не выводится из процента, который стоит у него сейчас.
   */
  const salariesQuery = useSalaryOverrides(me.data?.user.role === 'OWNER');

  /**
   * Прочие расходы месяца — оборудование, ремонт, разовые траты.
   *
   * Своя сумма в КАЖДОМ месяце, в отличие от аренды: покупку принтера
   * нельзя размазать по году — прошлые месяцы обеднели бы задним числом.
   */
  const otherQuery = useMonthExpenses(me.data?.user.role === 'OWNER');
  const otherByMonth = useMemo(() => {
    const map = new Map<string, { amountKop: number; note: string }>();
    for (const запись of otherQuery.data?.expenses ?? []) {
      map.set(запись.monthKey, { amountKop: запись.amountKop, note: запись.note });
    }
    return map;
  }, [otherQuery.data]);
  /** Прочие расходы месяца, копейки. Нет записи — нет и расхода. */
  const прочие = (ключ: string) => otherByMonth.get(ключ)?.amountKop ?? 0;
  const [otherBusy, setOtherBusy] = useState(false);

  /**
   * Записать расход открытого месяца.
   *
   * Мимо черновика раздела: это не правка настроек, которую копят, а
   * запись о состоявшейся трате — деньги уже потрачены.
   */
  const saveOther = async (kop: number, note: string) => {
    setOtherBusy(true);
    try {
      await api.put(`/api/month-expenses/${monthKey}`, { amountKop: Math.max(0, kop), note });
      void otherQuery.refetch();
    } catch (error) {
      show(error instanceof Error ? error.message : 'Не удалось записать расход', 'error');
    } finally {
      setOtherBusy(false);
    }
  };
  /** Кого правим: идентификатор сотрудника, пока открыто поле ввода. */
  const [editSalary, setEditSalary] = useState<{
    id: string;
    value: string;
    advance: string;
    note: string;
  } | null>(null);
  const [salaryBusy, setSalaryBusy] = useState(false);

  const [rentDraft, setRentDraft] = useState<number | null>(null);
  const [minSalaryDraft, setMinSalaryDraft] = useState<number | null>(null);
  /** Ставка налога, пока её правят и не сохранили. */
  const [taxDraft, setTaxDraft] = useState<number | null>(null);
  const rentKop = rentDraft ?? settings?.rentKop ?? 0;
  const minSalaryKop = minSalaryDraft ?? settings?.minSalaryKop ?? 0;

  const orders = useMemo(
    () =>
      (ordersQuery.data?.orders ?? []).map((order) => ({
        received: order.received,
        costKop: order.costKop,
        expenseKop: order.expenseKop,
        // Статус нужен ради «Ожидания»: замороженный заказ выпадает из расчёта.
        status: order.status,
      })),
    [ordersQuery.data],
  );

  const monthKey = monthKeyForOffset(today, offset);

  /**
   * Налог УСН считается ПО СЧЕТАМ, а не по выручке.
   *
   * Налоговую интересуют деньги, прошедшие по расчётному счёту, а не объём
   * принятой работы: заказы июня попадают в счёт от 2 июля, и налог за них
   * платится в третьем квартале. Наличные в базу не входят — счета по ним не
   * выставляются.
   */
  const taxPercent = taxDraft ?? settings?.taxPercent ?? 6;
  const счета = useMemo(
    () =>
      (invoicesQuery.data?.invoices ?? []).map((i) => ({
        date: i.date,
        totalKop: i.items.reduce((сумма, п) => сумма + Math.round(п.priceKop * п.qty), 0),
      })),
    [invoicesQuery.data],
  );
  const налог = useMemo(() => taxForMonth(счета, monthKey, taxPercent), [счета, monthKey, taxPercent]);

  /**
   * Замороженные заказы месяца — их в расчёте НЕТ.
   *
   * Без этой строки цифры расходились бы с журналом молча: в августе видно
   * 163 заказа, а в финансах — 160, и понять, куда делись три, было бы
   * неоткуда. Поэтому сумма отложенного показывается рядом с выручкой.
   */
  const frozen = useMemo(() => {
    const свои = orders.filter(
      (o) => o.received.slice(0, 7) === monthKey && o.status === 'Ожидание',
    );
    return { count: свои.length, sumKop: свои.reduce((s, o) => s + o.costKop, 0) };
  }, [orders, monthKey]);
  const current = monthTotals(orders, monthKey, rentKop, налог.taxKop, прочие(monthKey));
  const прошлыйМесяц = monthKeyForOffset(today, offset - 1);
  const previous = monthTotals(
    orders,
    прошлыйМесяц,
    rentKop,
    taxForMonth(счета, прошлыйМесяц, taxPercent).taxKop,
    прочие(прошлыйМесяц),
  );
  /*
    В ряду по месяцам налог у КАЖДОГО месяца свой: он считается по счетам
    этого месяца. Общий `monthlyTrend` этого не умеет — собираем ряд сами.
  */
  const trend = useMemo(
    () =>
      /*
        Ряд короче запрошенного, если упёрся в начало учёта: график за год
        из сентября 2026-го — это девять столбиков, а не двенадцать, три из
        которых 2025-й с нулями. Нули там читались бы как месяцы без работы.
      */
      monthKeysUpTo(monthKeyForOffset(today, offset), span).map((ключ) => {
        return monthTotals(
          orders,
          ключ,
          rentKop,
          taxForMonth(счета, ключ, taxPercent).taxKop,
          прочие(ключ),
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orders, today, offset, span, rentKop, счета, taxPercent, otherByMonth],
  );

  /*
   * Владелец в списке зарплат НЕ участвует. Его деньги — это остаток внизу
   * расчёта; строка «Ханеев Д.О. · 0 % · 0 ₽» рядом с ним была пустой и
   * сбивала с толку, а если бы владелец поставил себе процент, доход
   * посчитался бы ему дважды.
   */
  const employees = (bootstrap.data?.employees ?? []).filter(
    (e) => e.role !== 'OWNER' && e.percent !== undefined && e.bonusKop !== undefined,
  );
  /** Назначенные суммы ЭТОГО месяца: ключ — сотрудник. */
  const manualThisMonth = useMemo(
    () => salariesForMonth(salariesQuery.data?.salaries ?? [], monthKey),
    [salariesQuery.data, monthKey],
  );

  const salaries = payroll(
    employees.map((e) => ({ id: e.id, name: e.name, percent: e.percent!, bonusKop: e.bonusKop! })),
    current.salaryBaseKop,
    minSalaryKop,
    manualThisMonth,
    current.incomeKop,
    monthKey,
  );

  /**
   * Сохранение одной величины настроек.
   *
   * В черновик загружаются ОБЕ правимые величины, а не только меняемая:
   * фиксация шлёт разницу с загруженным состоянием, и запись одной аренды
   * затёрла бы минимальную зарплату, если та менялась в этой же сессии.
   */
  /**
   * Записать фактическую выплату за месяц.
   *
   * Идёт мимо черновика модуля: это не правка справочника, которую копят и
   * фиксируют пачкой, а запись о состоявшемся событии — деньги уже отданы.
   */
  const saveSalary = async () => {
    if (!editSalary) return;
    setSalaryBusy(true);
    try {
      /*
       * Пустое поле суммы — это НЕ ноль, а «вручную не ставили»: итог
       * считается по проценту. Ноль пишут, когда за месяц не платили.
       */
      const суммаЕсть = editSalary.value.trim() !== '';
      const kop = rublesToKop(Number(editSalary.value.replace(',', '.')) || 0);
      const авансKop = rublesToKop(Number(editSalary.advance.replace(',', '.')) || 0);
      await api.put(`/api/salaries/${editSalary.id}/${monthKey}`, {
        amountKop: суммаЕсть ? kop : null,
        advanceKop: авансKop,
        // Отпускные меняются в «Сотрудниках»: сервер сохраняет их сам,
        // не перезаписывая потенциально устаревшим значением этого экрана.
        note: editSalary.note,
      });
      setEditSalary(null);
      void salariesQuery.refetch();
    } catch (error) {
      show(error instanceof Error ? error.message : 'Не удалось записать', 'error');
    } finally {
      setSalaryBusy(false);
    }
  };

  const clearSalary = async (employeeId: string) => {
    setSalaryBusy(true);
    try {
      await api.del(`/api/salaries/${employeeId}/${monthKey}`);
      show('Вернулись к расчёту');
      setEditSalary(null);
      void salariesQuery.refetch();
    } catch (error) {
      show(error instanceof Error ? error.message : 'Не удалось вернуть расчёт', 'error');
    } finally {
      setSalaryBusy(false);
    }
  };

  const saveSetting = async (
    patch: { rentKop?: number; minSalaryKop?: number; taxPercent?: number },
    label: string,
  ) => {
    if (!settings) return;
    useDrafts.getState().load('finance', 'settings', [
      {
        id: '1',
        version: settings.version,
        rentKop: settings.rentKop ?? 0,
        minSalaryKop: settings.minSalaryKop ?? 0,
        taxPercent: settings.taxPercent ?? 6,
      },
    ]);
    useDrafts.getState().update('finance', 'settings', '1', patch);

    const outcome = await module.save();
    setRentDraft(null);
    setMinSalaryDraft(null);
    setTaxDraft(null);
    if (outcome.ok) {
      void bootstrap.refetch();
    } else {
      show(outcome.error ?? 'Не удалось сохранить', 'error');
    }
  };

  /**
   * Выручка по клиникам за месяц — кто её принёс.
   *
   * Частные заказы (без клиники) не выбрасываются, а собираются одной
   * строкой: это тоже деньги, и в сумме они бывают заметны.
   *
   * Показываются первые шесть, остальные складываются в «прочие»: список из
   * тридцати строк перестаёт читаться, а хвост из мелких клиник интересен
   * только суммой.
   *
   * Расчёт стоит ЗДЕСЬ, выше выходов «идёт загрузка» и «только владельцу», и
   * ниже опускать его нельзя. React считает вызовы `useMemo` и требует, чтобы
   * их число не менялось от отрисовки к отрисовке. Когда этот вызов стоял под
   * выходами, первая отрисовка (данных ещё нет — вернули крутилку) насчитывала
   * их на один меньше следующей, и как только заказы приезжали, раздел падал с
   * «Rendered more hooks than during the previous render»: экран оставался
   * пустым до перезагрузки страницы. На быстрой связи это не проявлялось —
   * данные успевали лечь в память до первой отрисовки, и выхода не случалось.
   */
  const поКлиникам = useMemo(() => {
    const итого = new Map<string, number>();
    for (const o of ordersQuery.data?.orders ?? []) {
      if (o.received.slice(0, 7) !== monthKey) continue;
      if (o.status === 'Ожидание') continue;
      const имя = bootstrap.data?.clinics.find((c) => c.id === o.clinicId)?.name ?? 'Частные заказы';
      итого.set(имя, (итого.get(имя) ?? 0) + o.costKop);
    }

    const всего = [...итого.values()].reduce((сумма, значение) => сумма + значение, 0);
    if (!всего) return [] as { name: string; valueKop: number; доля: number; доляТочно: number }[];

    const строки = [...итого].sort((a, b) => b[1] - a[1]);
    const первые = строки.slice(0, 6);
    const хвост = строки.slice(6);
    if (хвост.length) {
      первые.push([`Прочие клиники (${хвост.length})`, хвост.reduce((сумма, [, значение]) => сумма + значение, 0)]);
    }

    return первые.map(([name, valueKop]) => ({
      name,
      valueKop,
      доля: Math.round((valueKop / всего) * 100),
      доляТочно: (valueKop / всего) * 100,
    }));
  }, [ordersQuery.data, bootstrap.data, monthKey]);

  /*
   * ЖДЁМ ВСЕ ЧЕТЫРЕ ЗАПРОСА, а не два.
   *
   * Раньше экран рисовался, как только приходили заказы, а фактические
   * зарплаты и прочие расходы ещё ехали. Первое число считалось без них: на
   * телефоне владелец успевал увидеть «Остаётся вам» с красной стрелкой вниз
   * — падение к прошлому месяцу, — и только потом, когда зарплаты приезжали,
   * то же число перекрашивалось в зелёное. Показывать заведомо неверные
   * деньги, пусть и полсекунды, нельзя: им верят.
   *
   * Счета в том же ряду — по ним считается налог, а он входит в доход.
   */
  const ждёмВладельца =
    me.data?.user.role === 'OWNER' && (salariesQuery.isPending || otherQuery.isPending);
  if (
    me.isLoading ||
    bootstrap.isLoading ||
    ordersQuery.isLoading ||
    invoicesQuery.isLoading ||
    ждёмВладельца
  ) {
    return <Spinner />;
  }

  /*
   * Неудачная загрузка ГОВОРИТ О СЕБЕ, а не притворяется пустым месяцем.
   *
   * Раньше при обрыве связи запрос отваливался, `data` оставалась пустой, и
   * раздел рисовал нули: выручка 0, доход равен минус аренде. Ошибка выглядела
   * как правдоподобный убыточный месяц — самый скверный вид отказа, потому что
   * поверить ему легко. Повтор берёт оба запроса разом: раздел без заказов и
   * без счетов одинаково нерабочий.
   */
  const сбой = ordersQuery.error ?? bootstrap.error ?? invoicesQuery.error;
  if (сбой) {
    return (
      <ErrorState
        error={сбой instanceof Error ? сбой : new Error('Не удалось загрузить финансы')}
        onRetry={() => {
          void bootstrap.refetch();
          void ordersQuery.refetch();
          void invoicesQuery.refetch();
        }}
      />
    );
  }

  // Дубль серверной проверки: прямой адрес /finance не должен ничего показать.
  if (me.data?.user.role !== 'OWNER') {
    return <p className="text-sm text-ink2">Раздел «Финансы» доступен только владельцу.</p>;
  }

  const incomeTrend = trendPercent(current.incomeKop, previous.incomeKop);
  /*
    У прошлого месяца — СВОИ назначенные суммы.

    Сравнение остатка владельца «месяц к месяцу» иначе считало бы прошлый
    месяц по расчёту, а текущий по факту, и стрелка роста показывала бы
    разницу способов подсчёта, а не разницу месяцев.
  */
  const previousMonthKey = monthKeyForOffset(today, offset - 1);
  const manualPrevMonth = salariesForMonth(salariesQuery.data?.salaries ?? [], previousMonthKey);

  const previousSalaries = payroll(
    employees.map((e) => ({ id: e.id, name: e.name, percent: e.percent!, bonusKop: e.bonusKop! })),
    previous.salaryBaseKop,
    minSalaryKop,
    manualPrevMonth,
    previous.incomeKop,
    previousMonthKey,
  );
  const remainderTrend = trendPercent(salaries.ownerRemainderKop, previousSalaries.ownerRemainderKop);

  /**
   * Доля от выручки в процентах.
   *
   * Считается ОТ ВЫРУЧКИ, а не от предыдущей строки: только так числа
   * складываются в понятную картину — «из ста рублей выручки двадцать восемь
   * ушло на материалы, семь на аренду, тридцать на зарплаты, тридцать пять
   * осталось». При нулевой выручке доли нет вовсе: делить не на что, а «0 %»
   * читалось бы как «расходов не было».
   */
  const доля = (kop: number): number | null =>
    current.revenueKop > 0 ? Math.round((Math.abs(kop) / current.revenueKop) * 100) : null;

  /** Длина полосок в расчёте — доля от выручки, самой большой величины месяца. */
  /**
   * Из чего складывается рубль выручки: расходы и остаток.
   *
   * Доли считаются от выручки, поэтому в сумме дают сотню — если месяц не
   * убыточный. При убытке остаток отрицателен, в полосу он не идёт, а рядом
   * печатается предупреждение словами: полоса, где расходы перевалили за сто
   * процентов, ничего не объясняет.
   */
  /**
   * Год целиком: двенадцать месяцев одной таблицей.
   *
   * Каждый месяц считается тем же кодом, что и открытый: доход после налога,
   * зарплаты — фактические там, где сумма записана вручную. Иначе годовой
   * итог разошёлся бы с помесячным, и доверять было бы нечему.
   *
   * Пустые месяцы пропускаются: строка из одних нулей только мешает, а
   * аренда за ненаступивший месяц показала бы убыток там, где работы не было.
   */
  const год = (() => {
    interface СтрокаГода {
      monthKey: string;
      revenueKop: number;
      expenseKop: number;
      rentKop: number;
      taxKop: number;
      otherKop: number;
      incomeKop: number;
      salariesKop: number;
      remainderKop: number;
    }
    const месяцы: СтрокаГода[] = [];
    for (let m = 1; m <= 12; m += 1) {
      const ключ = `${year}-${String(m).padStart(2, '0')}`;
      const taxKop = taxForMonth(счета, ключ, taxPercent).taxKop;
      const итоги = monthTotals(orders, ключ, rentKop, taxKop, прочие(ключ));
      if (итоги.orderCount === 0 && taxKop === 0) continue;

      const назначено = salariesForMonth(salariesQuery.data?.salaries ?? [], ключ);
      const расчёт = payroll(
        employees.map((e) => ({ id: e.id, name: e.name, percent: e.percent!, bonusKop: e.bonusKop! })),
        итоги.salaryBaseKop,
        minSalaryKop,
        назначено,
        итоги.incomeKop,
        ключ,
      );

      месяцы.push({
        monthKey: ключ,
        revenueKop: итоги.revenueKop,
        expenseKop: итоги.expenseKop,
        rentKop,
        taxKop,
        otherKop: итоги.otherKop,
        incomeKop: итоги.incomeKop,
        salariesKop: расчёт.totalSalariesKop,
        remainderKop: расчёт.ownerRemainderKop,
      });
    }

    const сложить = (поле: Exclude<keyof СтрокаГода, 'monthKey'>) =>
      месяцы.reduce((сумма, м) => сумма + м[поле], 0);

    return {
      месяцы,
      итог: {
        revenueKop: сложить('revenueKop'),
        expenseKop: сложить('expenseKop'),
        rentKop: сложить('rentKop'),
        taxKop: сложить('taxKop'),
        otherKop: сложить('otherKop'),
        incomeKop: сложить('incomeKop'),
        salariesKop: сложить('salariesKop'),
        remainderKop: сложить('remainderKop'),
      },
    };
  })();

  /** Годы, в которых есть заказы, плюс текущий — чтобы было куда встать. */
  const доступныеГоды = (() => {
    const набор = new Set(orders.map((o) => Number(o.received.slice(0, 4))));
    набор.add(Number(today.slice(0, 4)));
    return [...набор].sort((a, b) => b - a);
  })();

  const структура = [
    { label: 'Материалы', valueKop: current.expenseKop, color: FINANCE_COLORS.materials },
    { label: 'Аренда', valueKop: rentKop, color: FINANCE_COLORS.rent },
    /*
     * Налог — такой же расход рубля выручки, как аренда и зарплаты.
     *
     * Его здесь НЕ БЫЛО, и полоса не сходилась: в августе доли давали 93 %
     * вместо ста, а недостающие 8 % — 117 434 ₽ налога — просто отсутствовали.
     * Глазом это не ловится: полоса выглядела целой, а разница читалась как
     * остаток владельца, которого на деле нет.
     *
     * Порядок и цвет — как в расчёте выше: там налог стоит между арендой и
     * доходом и покрашен коралловым. Разойдись они, кусок полосы перестал бы
     * узнаваться в строке расчёта.
     */
    { label: 'Налог УСН', valueKop: налог.taxKop, color: FINANCE_COLORS.tax },
    { label: 'Прочие расходы', valueKop: прочие(monthKey), color: FINANCE_COLORS.other },
    { label: 'Зарплаты', valueKop: salaries.totalSalariesKop, color: FINANCE_COLORS.salaries },
    { label: 'Остаётся вам', valueKop: Math.max(0, salaries.ownerRemainderKop), color: FINANCE_COLORS.owner },
  ]
    .filter((ч) => ч.valueKop > 0)
    .map((ч) => ({
      ...ч,
      доля: Math.round((ч.valueKop / (current.revenueKop || 1)) * 100),
      доляТочно: Math.min(100, (ч.valueKop / (current.revenueKop || 1)) * 100),
    }));

  const scale = Math.max(current.revenueKop, 1);

  return (
    <div className="finance-screen">
      {/* Тот же кошелёк, что у пункта «Финансы» в меню. */}
      <SectionHeader
        icon={Wallet}
        title="Финансы"
        right={
          <div className="flex flex-wrap items-center gap-2">
            {offset !== 0 && (
              <button
                onClick={() => {
                  setOffset(0);
                  setEditSalary(null);
                }}
                className="rounded-lg border px-2.5 py-1.5 text-xs text-ink2 hover:bg-black/5"
                style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
              >
                Текущий месяц
              </button>
            )}
            {/* Левее января 2026 данных нет — и листать туда некуда. */}
            <MonthSwitcher
              label={formatMonthKey(monthKey)}
              onPrev={() => сменитьМесяц(-1)}
              onNext={() => сменитьМесяц(1)}
              prevDisabled={isFirstMonth(monthKey)}
            />
            {/* Масштаб — рядом с выбором месяца: оба управляют видом, а не
                данными. Сам переключатель размера не меняет. */}
            <ZoomControl zoom={zoom} hint="Мельче — расчёт и графики влезут целиком" />
          </div>
        }
      />

      {/*
        Два итога впереди расчёта: доход лаборатории и то, что из него остаётся
        владельцу. Всё остальное на странице объясняет, как получились эти два
        числа.
      */}
      {/*
        РАБОЧАЯ ОБЛАСТЬ раздела — под масштабом: итоги, цепочка расчёта,
        расходы и графики. Шапка с выбором месяца остаётся прежнего размера.
      */}
      <div style={{ zoom: `${zoom.zoom}%` }}>
      <div className="mb-5 grid gap-3 sm:grid-cols-2">
        <Hero
          label="Доход за месяц"
          valueKop={current.incomeKop}
          trend={incomeTrend}
          hint="после материалов, аренды, налога и прочих расходов"
        />
        <Hero
          label="Остаётся вам"
          valueKop={salaries.ownerRemainderKop}
          trend={remainderTrend}
          hint={employees.length > 0 ? 'доход минус зарплаты' : 'зарплаты не начисляются'}
          accent
        />
      </div>

      {monthKey === today.slice(0,7) && <p className="mb-4 text-xs text-ink2">Месяц ещё идёт · сравнение с полным предыдущим месяцем</p>}
      <div className="finance-pair">
      {/* Расчёт цепочкой — сама формула §4, строка за строкой. */}
      <div
        className="mb-5 rounded-xl border p-4 sm:p-5"
        style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
      >
        <p className="mb-3 text-[11px] font-medium uppercase tracking-wide text-ink2">
          Как получился остаток
        </p>

        <FlowRow
          label="Выручка"
          note={
            frozen.count > 0
              ? `${current.orderCount} заказ(ов) принято в ${formatMonthKey(monthKey).toLowerCase()} · ${frozen.count} в ожидании не учтены (${formatKop(frozen.sumKop)} ₽)`
              : `${current.orderCount} заказ(ов) принято в ${formatMonthKey(monthKey).toLowerCase()}`
          }
          valueKop={current.revenueKop}
          scale={scale}
          color={FINANCE_COLORS.revenue}
          share={current.revenueKop > 0 ? 100 : null}
        />
        <FlowRow
          label="Себестоимость"
          note="материалы по заказам месяца"
          valueKop={current.expenseKop}
          scale={scale}
          color={FINANCE_COLORS.materials}
          negative
          share={доля(current.expenseKop)}
        />
        <RentRow
          rentKop={rentKop}
          scale={scale}
          share={доля(rentKop)}
          saving={module.saving}
          draft={rentDraft}
          onDraft={setRentDraft}
          onSave={(value) => saveSetting({ rentKop: value }, 'Аренда')}
        />

        <TaxRow
          taxKop={налог.taxKop}
          baseKop={налог.baseKop}
          percent={taxPercent}
          scale={scale}
          share={доля(налог.taxKop)}
          saving={module.saving}
          draft={taxDraft}
          onDraft={setTaxDraft}
          onSave={(value) => saveSetting({ taxPercent: value }, 'Ставка налога')}
        />

        {/*
          ЗАРПЛАТЫ — перед прочими расходами, и итог в цепочке ОДИН.

          Раньше между ними стояла строка «= Доход». Она повторяла число,
          которое и так крупно написано плиткой вверху страницы, а под ней
          через строку шло «Остаётся вам» — два итога подряд, и приходилось
          вспоминать, чем один отличается от другого.

          Сам расчёт не изменился: остаток по-прежнему равен выручке за
          вычетом себестоимости, аренды, налога, зарплат и прочих трат.
          Убрана только промежуточная черта.
        */}
        {employees.length > 0 && (
          <FlowRow
            label="Зарплаты"
            note={`${employees.length} сотрудник(ов)`}
            valueKop={salaries.totalSalariesKop}
            scale={scale}
            color={FINANCE_COLORS.salaries}
            negative
            share={доля(salaries.totalSalariesKop)}
          />
        )}

        <OtherRow
          amountKop={прочие(monthKey)}
          note={otherByMonth.get(monthKey)?.note ?? ''}
          monthLabel={formatMonthKey(monthKey).toLowerCase()}
          scale={scale}
          share={доля(прочие(monthKey))}
          saving={otherBusy}
          onSave={(kop, note) => void saveOther(kop, note)}
        />

        <FlowTotal
          label="Остаётся вам"
          valueKop={salaries.ownerRemainderKop}
          strong
          share={доля(salaries.ownerRemainderKop)}
        />
      </div>

      {/*
        РАЗБОР В ДОЛЯХ — то же самое, но в процентах и рядом.

        Цепочка выше отвечает на вопрос «сколько», этот блок — «в какой
        пропорции». Одно без другого неполно: полмиллиона на материалы
        звучат одинаково при выручке в миллион и в три, а «28 % выручки»
        сравнимо и с прошлым месяцем, и с чужой лабораторией.

        Полоса набрана из тех же цветов, что и строки расчёта: глаз связывает
        кусок полосы со строкой без подписей.
      */}
      <div
        className="mb-5 rounded-xl border p-4 sm:p-5"
        style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
      >
        <p className="mb-3 text-[11px] font-medium uppercase tracking-wide text-ink2">
          Из чего складывается рубль выручки
        </p>

        {current.revenueKop > 0 ? (
          <>
            {salaries.ownerRemainderKop >= 0 && <div className="finance-ring" role="img" aria-label={`Распределение выручки. Остаётся вам: ${formatKop(salaries.ownerRemainderKop)} рублей`}>
              <svg viewBox="0 0 120 120" aria-hidden="true">{структура.map((часть,index)=>{const before=структура.slice(0,index).reduce((sum,p)=>sum+p.доляТочно,0);return <circle key={часть.label} cx="60" cy="60" r="48" fill="none" stroke={часть.color} strokeWidth="17" pathLength="100" strokeDasharray={`${часть.доляТочно} ${100-часть.доляТочно}`} strokeDashoffset={-before} transform="rotate(-90 60 60)"/>;})}</svg>
              <div><span>Остаётся вам</span><strong>{доля(salaries.ownerRemainderKop)}%</strong><span>{formatKop(salaries.ownerRemainderKop)} ₽</span></div>
            </div>}
            <div className="finance-allocation-legend grid gap-x-6 gap-y-2">
              {структура.map((часть) => (
                <div key={часть.label} className="flex items-baseline justify-between gap-3">
                  <span className="flex min-w-0 items-baseline gap-2">
                    <span
                      className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-sm"
                      style={{ background: часть.color }}
                    />
                    <span className="truncate text-sm text-ink">{часть.label}</span>
                  </span>
                  <span className="flex flex-shrink-0 items-baseline gap-2">
                    <span className="tabular text-sm font-medium text-ink">{часть.доля}%</span>
                    <span className="tabular text-xs text-ink2">{formatKop(часть.valueKop)} ₽</span>
                  </span>
                </div>
              ))}
            </div>

            {/*
              Убыточный месяц: доли расходов перевалили за сотню, и полоса
              перестаёт что-либо объяснять. Пишем словами, а не рисуем.
            */}
            {salaries.ownerRemainderKop < 0 && (
              <p className="mt-3 text-xs" style={{ color: STATUS_COLORS.danger }}>
                Расходы превысили выручку на {formatKop(-salaries.ownerRemainderKop)} ₽ — разницу
                покрываете вы.
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-ink2">
            В этом месяце заказов ещё нет — делить нечего.
          </p>
        )}
      </div>

      </div>
      <div className="finance-pair">
      {/*
        КТО ПРИНОСИТ ДЕНЬГИ.

        Второй вопрос после «куда уходит» — «откуда приходит». Клиники
        показаны долями от выручки месяца: так видно, что две трети оборота
        держатся на трёх клиниках, и чей уход был бы заметен.

        Показываются первые шесть, остальные складываются в «прочие»: список
        из тридцати строк перестаёт читаться, а хвост из мелких клиник
        интересен только суммой.
      */}
      {поКлиникам.length > 0 && (
        <div
          className="mb-5 rounded-xl border p-4 sm:p-5"
          style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
        >
          <p className="mb-3 text-[11px] font-medium uppercase tracking-wide text-ink2">
            Откуда пришла выручка
          </p>

          <div className="space-y-2">
            {поКлиникам.map((строка) => (
              <div key={строка.name}>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0 truncate text-sm text-ink">{строка.name}</span>
                  <span className="flex flex-shrink-0 items-baseline gap-2">
                    <span className="tabular text-sm font-medium text-ink">{строка.доля}%</span>
                    <span className="tabular text-xs text-ink2">{formatKop(строка.valueKop)} ₽</span>
                  </span>
                </div>
                <div
                  className="mt-1 h-1.5 overflow-hidden rounded-full"
                  style={{ background: 'var(--otc-bg)' }}
                >
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${строка.доляТочно}%`, background: 'var(--otc-accent)' }}
                  />
                </div>
              </div>
            ))}
          </div>

          <p className="mt-3 text-xs text-ink2">
            Доли считаются от выручки месяца. Частные заказы без клиники собраны в одну строку.
          </p>
        </div>
      )}

      {/* Зарплаты — разбор последней вычитаемой строки расчёта. */}
      {employees.length > 0 && (
        <div
          className="mb-5 rounded-xl border p-4 sm:p-5"
          style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
        >
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-ink2">
              Зарплаты за {formatMonthKey(monthKey).toLowerCase()}
            </p>
            <MinSalaryEditor
              minSalaryKop={minSalaryKop}
              saving={module.saving}
              onDraft={setMinSalaryDraft}
              onSave={(value) => saveSetting({ minSalaryKop: value }, 'Минимальная зарплата')}
            />
          </div>

          <div className="grid gap-2">
            {salaries.lines.map((line) => (
              <div
                key={line.employeeId}
                className="rounded-lg border p-3"
                style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-bg)' }}
              >
                <div className="mb-2 flex items-baseline justify-between gap-3">
                  <span className="min-w-0 truncate text-sm font-medium text-ink">{line.name}</span>
                  <span className="tabular flex-shrink-0 text-sm font-semibold text-ink">
                    {formatKop(line.salaryKop)} ₽
                  </span>
                </div>

                {/*
                  Фактическая выплата задаётся ЗДЕСЬ, за конкретный месяц.

                  Расчёт от процента и премии описывает, как платить дальше;
                  за прошедшие месяцы платили по своим таблицам учёта, и эти
                  суммы формулой не воспроизводятся. Поэтому здесь пишется
                  итог — а расчёт остаётся рядом, чтобы видеть разницу.
                */}
                {editSalary?.id === line.employeeId ? (
                  <div className="mb-2 flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <input
                        autoFocus
                        className="otc-input tabular w-28 px-2 py-1 text-xs"
                        placeholder="Сумма, ₽"
                        title="Ручная сумма зарплаты без отдельно указанных отпускных"
                        value={editSalary.value}
                        onChange={(e) => setEditSalary({ ...editSalary, value: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void saveSalary();
                          if (e.key === 'Escape') setEditSalary(null);
                        }}
                      />
                      {/* Аванс — тем же действием: это одно событие месяца. */}
                      <input
                        className="otc-input tabular w-24 px-2 py-1 text-xs"
                        placeholder="Аванс, ₽"
                        title="Аванс, выданный за этот месяц"
                        value={editSalary.advance}
                        onChange={(e) => setEditSalary({ ...editSalary, advance: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void saveSalary();
                          if (e.key === 'Escape') setEditSalary(null);
                        }}
                      />
                      <input
                        className="otc-input min-w-0 flex-1 px-2 py-1 text-xs"
                        placeholder="Откуда сумма (необязательно)"
                        value={editSalary.note}
                        onChange={(e) => setEditSalary({ ...editSalary, note: e.target.value })}
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <button
                        onClick={() => void saveSalary()}
                        disabled={salaryBusy}
                        className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50"
                        style={{ background: 'var(--otc-accent)' }}
                      >
                        <Check size={12} /> {salaryBusy ? 'Запись…' : 'Записать'}
                      </button>
                      <button
                        onClick={() => setEditSalary(null)}
                        className="rounded-lg border px-2.5 py-1 text-xs text-ink2 hover:bg-black/5"
                        style={{ borderColor: 'var(--otc-line)' }}
                      >
                        Отмена
                      </button>
                      {line.manual && (
                        <button
                          onClick={() => void clearSalary(line.employeeId)}
                          disabled={salaryBusy}
                          className="rounded-lg border px-2.5 py-1 text-xs text-ink2 hover:bg-black/5 disabled:opacity-50"
                          style={{ borderColor: 'var(--otc-line)' }}
                          title="Убрать назначенную сумму и вернуться к расчёту по проценту"
                        >
                          Вернуть расчёт
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() =>
                      setEditSalary({
                        id: line.employeeId,
                        value: manualSalaryInput(manualThisMonth.get(line.employeeId)),
                        advance: line.advanceKop ? String(line.advanceKop / 100) : '',
                        note: line.manualNote,
                      })
                    }
                    className="mb-2 inline-flex items-center gap-1 text-[11px] transition-colors hover:underline"
                    style={{ color: line.manual ? STATUS_COLORS.warning : 'var(--otc-ink2)' }}
                    title="Указать фактически выплаченную сумму за этот месяц"
                  >
                    <Pencil size={11} />
                    {line.manual ? 'сумма указана вручную' : 'указать фактическую сумму'}
                  </button>
                )}

                {/*
                  Разбивка нужна потому, что итог не выводится из одной строки:
                  премия начисляется даже при отрицательном доходе, а доля от
                  процента может вообще не участвовать — если она не дотянула
                  до гарантии, платится гарантия. Без разбивки владелец видел
                  бы сумму, не сходящуюся ни с процентом, ни с минимумом.
                */}
                <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-ink2">
                  {/*
                    «По расчёту вышло бы» — про месяцы с долей. До сентября
                    2026 расчёта не было вовсе: сумму вносили руками, и
                    сравнивать её не с чем.
                  */}
                  {line.manual && line.shareApplies && (
                    <span style={{ color: STATUS_COLORS.warning }}>
                      по расчёту вышло бы <span className="tabular">{formatKop(line.computedKop)} ₽</span>
                      {line.manualNote ? ` · ${line.manualNote}` : ''}
                    </span>
                  )}
                  {line.manual && !line.shareApplies && line.manualNote && (
                    <span className="text-ink2">{line.manualNote}</span>
                  )}
                  {line.advanceKop > 0 && (
                    <span style={{ color: STATUS_COLORS.warning }}>
                      аванс <span className="tabular">{formatKop(line.advanceKop)} ₽</span> · остаток{' '}
                      <span className="tabular">{formatKop(line.remainderKop)} ₽</span>
                    </span>
                  )}
                  {line.vacationKop > 0 && (
                    <span>
                      отпускные сверх зарплаты · <span className="tabular">{formatKop(line.vacationKop)} ₽</span>
                    </span>
                  )}
                  {line.shareApplies ? (
                    <span className={line.atMinimum ? 'line-through opacity-60' : undefined}>
                      {formatPercent(line.percent)}% от дохода ·{' '}
                      <span className="tabular">{formatKop(line.shareKop)} ₽</span>
                    </span>
                  ) : (
                    <span>выплачено вручную · доля считается с сентября 2026</span>
                  )}
                  {line.atMinimum && (
                    <span style={{ color: STATUS_COLORS.warning }}>
                      минимум · <span className="tabular">{formatKop(line.baseKop)} ₽</span>
                    </span>
                  )}
                  {line.bonusKop !== 0 && (
                    <span>
                      премия · <span className="tabular">{formatKop(line.bonusKop)} ₽</span>
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      </div>
      {/*
        ГОД ЦЕЛИКОМ — двенадцать месяцев в одной таблице.

        Помесячный разбор отвечает на вопрос «как прошёл этот месяц», годовой —
        «как идёт год»: виден сезон (январь всегда провальный, лето тянет),
        видно, сколько всего ушло налогов и сколько осталось. Без такой
        таблицы это считалось в тетради, переключая месяцы стрелками.

        Аренда в каждой строке одинаковая — она постоянная. Если её меняли,
        прошлые месяцы пересчитаются по нынешней: программа хранит одно
        значение, и это известное ограничение.
      */}
      <div
        className="mb-5 rounded-xl border p-4 sm:p-5"
        style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
      >
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-[11px] font-medium uppercase tracking-wide text-ink2">
            Год целиком
          </p>
          <div className="flex gap-1">
            {доступныеГоды.map((год) => (
              <button
                key={год}
                onClick={() => setYear(год)}
                className="rounded-md border px-2.5 py-1 text-xs"
                style={{
                  borderColor: year === год ? 'var(--otc-accent)' : 'var(--otc-line)',
                  color: year === год ? '#fff' : 'var(--otc-ink2)',
                  background: year === год ? 'var(--otc-accent)' : 'transparent',
                }}
              >
                {год}
              </button>
            ))}
          </div>
        </div>

        {/*
          Таблица прокручивается вбок внутри себя: семь колонок с суммами не
          помещаются в телефон никакими ухищрениями, а прятать столбцы —
          значит прятать деньги.
        */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ borderCollapse: 'collapse', minWidth: 700 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--otc-line)' }}>
                {[
                  'Месяц',
                  'Выручка',
                  'Материалы',
                  'Аренда',
                  'Налог',
                  'Прочее',
                  'Доход',
                  'Зарплаты',
                  'Остаётся',
                ].map(
                  (заголовок, i) => (
                    <th
                      key={заголовок}
                      /*
                       * Столбец месяца ПРИКЛЕЕН к левому краю.
                       *
                       * Семь колонок с суммами в телефон не помещаются, и
                       * таблицу приходится листать вбок. Уезжай месяц вместе с
                       * ними — на экране остался бы ряд чисел без указания, к
                       * какому месяцу он относится, и таблица стала бы
                       * нечитаемой ровно там, где её листают.
                       */
                      className={`whitespace-nowrap px-2 py-2 text-[11px] font-medium uppercase tracking-wide text-ink2 ${
                        i === 0 ? 'sticky left-0 z-10 text-left' : 'text-right'
                      }`}
                      style={i === 0 ? { background: 'var(--otc-surface)' } : undefined}
                    >
                      {заголовок}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {год.месяцы.map((строка) => (
                <tr
                  key={строка.monthKey}
                  className={строка.monthKey === monthKey ? 'font-medium' : undefined}
                  style={{
                    borderBottom: '1px solid var(--otc-line)',
                    background: строка.monthKey === monthKey ? 'var(--otc-bg)' : undefined,
                  }}
                >
                  <td
                    className="sticky left-0 z-10 whitespace-nowrap px-2 py-1.5 text-left first-letter:uppercase"
                    /*
                     * Своя заливка обязательна: приклеенная ячейка едет ПОВЕРХ
                     * остальных, и без непрозрачного фона сквозь неё
                     * просвечивали бы уезжающие под неё суммы.
                     */
                    style={{
                      background:
                        строка.monthKey === monthKey ? 'var(--otc-bg)' : 'var(--otc-surface)',
                    }}
                  >
                    {formatMonthKey(строка.monthKey).split(' ')[0]}
                  </td>
                  <td className="tabular px-2 py-1.5 text-right text-ink">{formatKop(строка.revenueKop)}</td>
                  <td className="tabular px-2 py-1.5 text-right text-ink2">{formatKop(строка.expenseKop)}</td>
                  <td className="tabular px-2 py-1.5 text-right text-ink2">{formatKop(строка.rentKop)}</td>
                  <td className="tabular px-2 py-1.5 text-right" style={{ color: FINANCE_COLORS.tax }}>
                    {formatKop(строка.taxKop)}
                  </td>
                  <td className="tabular px-2 py-1.5 text-right text-ink2">
                    {formatKop(строка.otherKop)}
                  </td>
                  <td className="tabular px-2 py-1.5 text-right text-ink">{formatKop(строка.incomeKop)}</td>
                  <td className="tabular px-2 py-1.5 text-right text-ink2">{formatKop(строка.salariesKop)}</td>
                  <td
                    className="tabular px-2 py-1.5 text-right font-medium"
                    style={{ color: строка.remainderKop < 0 ? STATUS_COLORS.danger : STATUS_COLORS.done }}
                  >
                    {formatKop(строка.remainderKop)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid var(--otc-line)' }}>
                <td
                  className="sticky left-0 z-10 px-2 py-2 text-left text-sm font-semibold text-ink"
                  style={{ background: 'var(--otc-surface)' }}
                >
                  Всего
                </td>
                <td className="tabular px-2 py-2 text-right font-semibold text-ink">{formatKop(год.итог.revenueKop)}</td>
                <td className="tabular px-2 py-2 text-right text-ink2">{formatKop(год.итог.expenseKop)}</td>
                <td className="tabular px-2 py-2 text-right text-ink2">{formatKop(год.итог.rentKop)}</td>
                <td className="tabular px-2 py-2 text-right font-semibold" style={{ color: FINANCE_COLORS.tax }}>
                  {formatKop(год.итог.taxKop)}
                </td>
                <td className="tabular px-2 py-2 text-right text-ink2">
                  {formatKop(год.итог.otherKop)}
                </td>
                <td className="tabular px-2 py-2 text-right font-semibold text-ink">{formatKop(год.итог.incomeKop)}</td>
                <td className="tabular px-2 py-2 text-right text-ink2">{formatKop(год.итог.salariesKop)}</td>
                <td
                  className="tabular px-2 py-2 text-right font-semibold"
                  style={{ color: год.итог.remainderKop < 0 ? STATUS_COLORS.danger : STATUS_COLORS.done }}
                >
                  {formatKop(год.итог.remainderKop)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <p className="mt-3 text-xs text-ink2">
          Зарплаты — фактические там, где сумма записана вручную, и расчётные в остальных
          месяцах. Налог считается по счетам месяца, заказы в «Ожидании» не участвуют нигде.
          Месяцы без заказов пропущены.
        </p>
      </div>

      {/* График */}
      <div
        className="rounded-xl border p-4 sm:p-5"
        style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
      >
        <div className="mb-3 flex items-center justify-between">
          <p className="text-[11px] font-medium uppercase tracking-wide text-ink2">Динамика</p>
          <div className="flex gap-1">
            {[3, 6, 12].map((value) => (
              <button
                key={value}
                onClick={() => setSpan(value)}
                className="rounded-md border px-2 py-1 text-xs"
                style={{
                  borderColor: span === value ? 'var(--otc-accent)' : 'var(--otc-line)',
                  color: span === value ? '#fff' : 'var(--otc-ink2)',
                  background: span === value ? 'var(--otc-accent)' : 'transparent',
                }}
              >
                {value} мес.
              </button>
            ))}
          </div>
        </div>

        {/*
          Доход — ЛИНИЕЙ, а не третьим столбиком. Три столбика подряд читались
          как три независимые величины, хотя доход — это разность двух других;
          линия поверх них показывает именно остаток.
        */}
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart
            data={trend.map((t) => ({
              month: formatMonthKey(t.monthKey).split(' ')[0],
              Выручка: t.revenueKop / 100,
              Себестоимость: t.expenseKop / 100,
              Доход: t.incomeKop / 100,
            }))}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--otc-line)" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="var(--otc-ink2)" />
            <YAxis
              tick={{ fontSize: 11 }}
              stroke="var(--otc-ink2)"
              tickFormatter={(value: number) =>
                /*
                 * Сокращение до тысяч работает и для ОТРИЦАТЕЛЬНЫХ значений.
                 *
                 * Сравнение шло без модуля, и убыточные месяцы подписывались
                 * полным числом: сверху «90к», снизу «−90000». Разнобой читался
                 * как ошибка, а длинная подпись съедала ширину — на телефоне
                 * это прямой отъём места у самого графика.
                 */
                Math.abs(value) >= 1000 ? `${Math.round(value / 1000)}к` : String(value)
              }
            />
            <Legend wrapperStyle={{fontSize:12}}/>
            <Tooltip
              formatter={(value: number) => `${value.toLocaleString('ru-RU')} ₽`}
              contentStyle={{
                borderColor: 'var(--otc-line)',
                borderRadius: 8,
                background: 'var(--otc-surface)',
                fontSize: 12,
              }}
            />
            <Bar dataKey="Выручка" fill={FINANCE_COLORS.revenue} radius={[3, 3, 0, 0]} />
            <Bar dataKey="Себестоимость" fill={FINANCE_COLORS.materials} radius={[3, 3, 0, 0]} />
            <Line
              type="monotone"
              dataKey="Доход"
              stroke={FINANCE_COLORS.owner}
              strokeWidth={2}
              dot={{ r: 3, fill: FINANCE_COLORS.owner }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      </div>

      <Toast toast={toast} />
    </div>
  );
}

/** Крупный итог месяца. */
function Hero({
  label,
  valueKop,
  trend,
  hint,
  accent,
}: {
  label: string;
  valueKop: number;
  trend: number | null;
  hint: string;
  accent?: boolean;
}) {
  const negative = valueKop < 0;
  return (
    <div
      className="finance-hero rounded-xl border p-4 sm:p-5"
      style={{
        borderColor: accent ? 'var(--otc-accent)' : 'var(--otc-line)',
        background: 'var(--otc-surface)',
      }}
    >
      <p className="text-xs text-ink2">{label}</p>
      <p
        className="tabular mt-1 text-2xl font-semibold sm:text-3xl"
        style={{ color: negative ? STATUS_COLORS.danger : accent ? STATUS_COLORS.done : 'var(--otc-ink)' }}
      >
        {formatKop(valueKop)} ₽
      </p>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 text-xs text-ink2">
        <span>{hint}</span>
        {trend !== null && (
          <span style={{ color: trend >= 0 ? STATUS_COLORS.done : STATUS_COLORS.danger }}>
            {trend >= 0 ? '▲' : '▼'} {Math.abs(trend)}% к прошлому месяцу
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Строка расчёта: подпись, полоска в масштабе выручки и сумма.
 *
 * Полоска нужна не для красоты: рядом стоящие «412 500» и «210 160» на глаз
 * почти одинаковы, а по длине сразу видно, сколько съедает себестоимость.
 */
function FlowRow({
  label,
  note,
  valueKop,
  scale,
  color,
  negative,
  action,
  share,
}: {
  label: string;
  note: string;
  valueKop: number;
  scale: number;
  color: string;
  negative?: boolean;
  action?: React.ReactNode;
  /** Доля от выручки в процентах. `null` — считать не от чего. */
  share?: number | null;
}) {
  const width = Math.min(100, Math.round((Math.abs(valueKop) / scale) * 100));
  return (
    <div className="finance-flow-row py-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="flex min-w-0 items-baseline gap-2">
          <span className="truncate text-sm text-ink">{label}</span>
          {action}
        </span>
        <span className="flex flex-shrink-0 items-baseline gap-2">
          {/*
            Доля СПРАВА ОТ СУММЫ, мелко и приглушённо.

            Рубли — то, что смотрят в первую очередь; процент отвечает на
            следующий вопрос: «а это много?». Пятьсот тысяч себестоимости
            звучат одинаково и при выручке в миллион, и при трёх, а «28 % от
            выручки» — уже оценка, с которой можно сравнить прошлый месяц.
          */}
          {share !== null && (
            <span className="tabular text-xs text-ink2">{share}%</span>
          )}
          <span className="tabular text-sm text-ink">
            {negative ? '− ' : ''}
            {formatKop(valueKop)} ₽
          </span>
        </span>
      </div>

      <p className="mt-1 text-xs text-ink2">{note}</p>
    </div>
  );
}

/** Черта и итог: «= Доход», «= Остаётся вам». */
function FlowTotal({
  label,
  valueKop,
  strong,
  share,
}: {
  label: string;
  valueKop: number;
  strong?: boolean;
  /** Доля от выручки: у итога она отвечает на вопрос «сколько остаётся с рубля». */
  share?: number | null;
}) {
  const negative = valueKop < 0;
  return (
    <div
      className="mt-2 flex items-baseline justify-between gap-3 border-t pt-2.5"
      style={{ borderColor: 'var(--otc-line)' }}
    >
      <span className={`text-sm ${strong ? 'font-semibold' : 'font-medium'} text-ink`}>{label}</span>
      <span className="flex items-baseline gap-2">
        {share !== null && share !== undefined && (
          <span className="tabular text-xs text-ink2">{share}%</span>
        )}
        <span
          className={`tabular text-sm ${strong ? 'font-semibold' : 'font-medium'}`}
          style={{ color: negative ? STATUS_COLORS.danger : strong ? STATUS_COLORS.done : 'var(--otc-ink)' }}
        >
          {formatKop(valueKop)} ₽
        </span>
      </span>
    </div>
  );
}

/**
 * Аренда — единственная правимая величина расчёта.
 *
 * Правится прямо в строке, где участвует: отдельное поле под карточками
 * стояло в стороне от чисел, на которые влияет, и связь читалась не сразу.
 */
/**
 * Минимальная зарплата — правится там, где действует.
 *
 * Стоит в шапке блока зарплат, а не в строке расчёта дохода: на доход она не
 * влияет вовсе, она перераспределяет уже посчитанный доход. Поставь её рядом
 * с арендой — читалось бы как ещё один вычитаемый расход, чем она не является.
 */
function MinSalaryEditor({
  minSalaryKop,
  saving,
  onDraft,
  onSave,
}: {
  minSalaryKop: number;
  saving: boolean;
  onDraft: (value: number | null) => void;
  onSave: (value: number) => void;
}) {
  const [editing, setEditing] = useState(false);

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="flex items-center gap-1.5 rounded-lg border px-2 py-1 text-xs text-ink2 hover:bg-black/5"
        style={{ borderColor: 'var(--otc-line)' }}
        title="Изменить минимальную зарплату"
      >
        <span>минимум</span>
        <span className="tabular font-medium text-ink">{formatKop(minSalaryKop)} ₽</span>
        <Pencil size={11} className="text-ink2" />
      </button>
    );
  }

  const close = () => {
    onDraft(null);
    setEditing(false);
  };

  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-ink2">минимум</span>
      <input
        autoFocus
        className="otc-input tabular max-w-[7.5rem] py-1 text-xs"
        value={formatKop(minSalaryKop)}
        onChange={(e) => onDraft(rublesToKop(e.target.value))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onSave(minSalaryKop);
            setEditing(false);
          }
          if (e.key === 'Escape') close();
        }}
      />
      <button
        onClick={() => {
          onSave(minSalaryKop);
          setEditing(false);
        }}
        disabled={saving}
        className="rounded-lg border p-1 hover:bg-black/5 disabled:opacity-50"
        style={{ borderColor: 'var(--otc-line)' }}
        title="Сохранить минимальную зарплату"
      >
        <Check size={13} className="text-ink" />
      </button>
      <button
        onClick={close}
        className="rounded-lg border p-1 hover:bg-black/5"
        style={{ borderColor: 'var(--otc-line)' }}
        title="Отменить"
      >
        <X size={13} className="text-ink2" />
      </button>
    </div>
  );
}

/**
 * Налог УСН — строка расчёта со своей, отдельной базой.
 *
 * Все прочие строки считаются от заказов месяца, эта — от выставленных
 * счетов, и это не оплошность, а суть налога: платят с того, что прошло по
 * расчётному счёту. Поэтому под строкой подписано, от какой суммы взяты
 * проценты, — иначе число выглядит взятым ниоткуда и не сходится ни с
 * выручкой, ни с долгом.
 *
 * Правится ставка, а не сумма: сумма — следствие. Ставка хранится в
 * настройках, потому что зависит от режима и региона, а не от месяца.
 */
function TaxRow({
  taxKop,
  baseKop,
  percent,
  scale,
  share,
  saving,
  draft,
  onDraft,
  onSave,
}: {
  taxKop: number;
  /** Сумма счетов месяца, с которой взят налог. */
  baseKop: number;
  percent: number;
  scale: number;
  share?: number | null;
  saving: boolean;
  draft: number | null;
  onDraft: (value: number | null) => void;
  onSave: (value: number) => void;
}) {
  const [editing, setEditing] = useState(false);

  const подпись =
    baseKop > 0
      ? `${formatPercent(percent)}% со счетов месяца — ${formatKop(baseKop)} ₽`
      : 'счетов в этом месяце пока нет';

  if (editing) {
    return (
      <div className="py-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-ink">Налог УСН</span>
          <input
            autoFocus
            className="otc-input tabular w-20 px-2 py-1 text-sm"
            value={draft ?? percent}
            onChange={(e) => onDraft(Number(e.target.value.replace(',', '.')) || 0)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onSave(draft ?? percent);
                setEditing(false);
              }
              if (e.key === 'Escape') {
                onDraft(null);
                setEditing(false);
              }
            }}
          />
          <span className="text-sm text-ink2">%</span>
          <button
            onClick={() => {
              onSave(draft ?? percent);
              setEditing(false);
            }}
            disabled={saving}
            className="rounded-lg border p-1.5 hover:bg-black/5 disabled:opacity-50"
            style={{ borderColor: 'var(--otc-line)' }}
            title="Сохранить ставку"
          >
            <Check size={14} className="text-ink" />
          </button>
          <button
            onClick={() => {
              onDraft(null);
              setEditing(false);
            }}
            className="rounded-lg border p-1.5 hover:bg-black/5"
            style={{ borderColor: 'var(--otc-line)' }}
            title="Отменить"
          >
            <X size={14} className="text-ink2" />
          </button>
        </div>
        <p className="mt-1 text-xs text-ink2">
          {draft !== null ? 'Enter — сохранить, Esc — отменить' : подпись}
        </p>
      </div>
    );
  }

  return (
    <FlowRow
      share={share}
      label="Налог УСН"
      note={подпись}
      valueKop={taxKop}
      scale={scale}
      color={FINANCE_COLORS.tax}
      negative
      action={
        <button
          onClick={() => setEditing(true)}
          className="rounded p-1 hover:bg-black/5"
          title="Изменить ставку налога"
        >
          <Pencil size={12} className="text-ink2" />
        </button>
      }
    />
  );
}

/**
 * Прочие расходы месяца — строка расчёта, правимая на месте.
 *
 * От аренды отличается тем, что сумма СВОЯ в каждом месяце: аренда
 * постоянна, а покупка оборудования случается однажды. Поэтому рядом с
 * суммой живёт приписка «за что» — через полгода «−120 000» без пояснения
 * не значит ничего.
 */
function OtherRow({
  amountKop,
  note,
  monthLabel,
  scale,
  share,
  saving,
  onSave,
}: {
  amountKop: number;
  note: string;
  monthLabel: string;
  scale: number;
  share?: number | null;
  saving: boolean;
  onSave: (kop: number, note: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(0);
  const [draftNote, setDraftNote] = useState('');

  const start = () => {
    setDraft(amountKop);
    setDraftNote(note);
    setEditing(true);
  };

  const finish = () => {
    onSave(draft, draftNote);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="py-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-ink">Другие расходы</span>
          <input
            autoFocus
            className="otc-input tabular max-w-[9rem]"
            value={formatKop(draft)}
            onChange={(e) => setDraft(rublesToKop(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') finish();
              if (e.key === 'Escape') setEditing(false);
            }}
          />
          <input
            className="otc-input min-w-0 flex-1"
            placeholder="За что (необязательно)"
            value={draftNote}
            onChange={(e) => setDraftNote(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') finish();
              if (e.key === 'Escape') setEditing(false);
            }}
          />
          <button
            onClick={finish}
            disabled={saving}
            className="rounded-lg border p-1.5 hover:bg-black/5 disabled:opacity-50"
            style={{ borderColor: 'var(--otc-line)' }}
            title="Записать расход"
          >
            <Check size={14} className="text-ink" />
          </button>
          <button
            onClick={() => setEditing(false)}
            className="rounded-lg border p-1.5 hover:bg-black/5"
            style={{ borderColor: 'var(--otc-line)' }}
            title="Отменить"
          >
            <X size={14} className="text-ink2" />
          </button>
        </div>
        <p className="mt-1 text-xs text-ink2">Enter — записать, Esc — отменить</p>
      </div>
    );
  }

  return (
    <FlowRow
      share={share}
      label="Другие расходы"
      note={note || `разовые траты за ${monthLabel}`}
      valueKop={amountKop}
      scale={scale}
      color={FINANCE_COLORS.other}
      negative
      action={
        <button
          onClick={start}
          className="rounded p-1 hover:bg-black/5"
          title="Изменить расходы месяца"
        >
          <Pencil size={12} className="text-ink2" />
        </button>
      }
    />
  );
}

function RentRow({
  rentKop,
  scale,
  share,
  saving,
  draft,
  onDraft,
  onSave,
}: {
  rentKop: number;
  scale: number;
  /** Доля аренды в выручке месяца. */
  share?: number | null;
  saving: boolean;
  draft: number | null;
  onDraft: (value: number | null) => void;
  onSave: (value: number) => void;
}) {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <div className="py-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-ink">Аренда</span>
          <input
            autoFocus
            className="otc-input tabular max-w-[9rem]"
            value={formatKop(rentKop)}
            onChange={(e) => onDraft(rublesToKop(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onSave(rentKop);
                setEditing(false);
              }
              if (e.key === 'Escape') {
                onDraft(null);
                setEditing(false);
              }
            }}
          />
          <button
            onClick={() => {
              onSave(rentKop);
              setEditing(false);
            }}
            disabled={saving}
            className="rounded-lg border p-1.5 hover:bg-black/5 disabled:opacity-50"
            style={{ borderColor: 'var(--otc-line)' }}
            title="Сохранить аренду"
          >
            <Check size={14} className="text-ink" />
          </button>
          <button
            onClick={() => {
              onDraft(null);
              setEditing(false);
            }}
            className="rounded-lg border p-1.5 hover:bg-black/5"
            style={{ borderColor: 'var(--otc-line)' }}
            title="Отменить"
          >
            <X size={14} className="text-ink2" />
          </button>
        </div>
        <p className="mt-1 text-xs text-ink2">
          {draft !== null ? 'Enter — сохранить, Esc — отменить' : 'постоянные расходы месяца'}
        </p>
      </div>
    );
  }

  return (
    <FlowRow
      share={share}
      label="Аренда"
      note="постоянные расходы месяца"
      valueKop={rentKop}
      scale={scale}
      color={FINANCE_COLORS.rent}
      negative
      action={
        <button
          onClick={() => setEditing(true)}
          className="rounded p-1 hover:bg-black/5"
          title="Изменить аренду"
        >
          <Pencil size={12} className="text-ink2" />
        </button>
      }
    />
  );
}
